import { devices_settings } from 'src/stores';
import type { Device } from 'src/types/Device';

const PHASE_LETTERS = ['a', 'b', 'c'] as const;

const usableName = (value: unknown): string | undefined => {
	if (typeof value !== 'string') return undefined;
	const name = value.trim();
	return name && name.toLowerCase() !== 'unnamed' ? name : undefined;
};

/** Resolve user-configured EM3 names across Gen1 arrays and newer component settings. */
export const resolveConfiguredEm3ChannelNames = (
	device: Pick<Device, 'id'>,
	settings: Record<string, any> | undefined,
): Array<string | undefined> => {
	const deviceRecord = device as Record<string, any>;
	const consolidated = settings?.['em:0'] ?? settings?.['emeter:0'];

	return PHASE_LETTERS.map((letter, index) => {
		const component = settings?.[`em:${index}`] ?? settings?.[`emeter:${index}`];
		return (
			usableName(settings?.emeters?.[index]?.name) ??
			usableName(settings?.emeter?.[index]?.name) ??
			usableName(consolidated?.phase_names?.[index]) ??
			usableName(consolidated?.[`phase_${letter}_name`]) ??
			usableName(settings?.phase_names?.[index]) ??
			usableName(settings?.channel_names?.[index]) ??
			usableName(component?.name) ??
			usableName(deviceRecord.phase_names?.[index]) ??
			usableName(deviceRecord.channel_names?.[index])
		);
	});
};

export const getConfiguredEm3ChannelNames = (device: Pick<Device, 'id'>): Array<string | undefined> =>
	resolveConfiguredEm3ChannelNames(device, devices_settings.getByRealId(device.id));

export const getConfiguredEm3ChannelName = (
	device: Pick<Device, 'id'>,
	channel: number,
): string | undefined => getConfiguredEm3ChannelNames(device)[channel];

export const applyResolvedEm3ChannelLabels = <T extends { name: string; label?: string }>(
	series: T[],
	aliases: Array<string | undefined>,
	formatSeriesLabel: (name: string) => string,
): T[] => {
	if (!aliases.some(Boolean)) return series;

	return series.map((item) => {
		const match = item.name.match(/^phase_([abc])(?:_|$)/);
		if (!match) return item;
		const index = PHASE_LETTERS.indexOf(match[1] as (typeof PHASE_LETTERS)[number]);
		const alias = aliases[index];
		if (!alias) return item;

		const defaultPhaseLabel = formatSeriesLabel(`phase_${match[1]}`);
		const formattedLabel = formatSeriesLabel(item.name);
		const label = formattedLabel.includes(defaultPhaseLabel)
			? formattedLabel.replace(defaultPhaseLabel, alias)
			: item.name === `phase_${match[1]}`
				? alias
				: `${alias} · ${formattedLabel}`;
		return { ...item, label };
	});
};

export const applyEm3ChannelLabels = <T extends { name: string; label?: string }>(
	series: T[],
	device: Pick<Device, 'id'>,
	formatSeriesLabel: (name: string) => string,
): T[] => applyResolvedEm3ChannelLabels(series, getConfiguredEm3ChannelNames(device), formatSeriesLabel);
